import { useCallback, useEffect, useMemo, useState } from 'react'
import * as Sentry from '@sentry/react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { billingView, formatBillingDate } from '../lib/billingStatus'
import useTenantVertical from '../hooks/useTenantVertical'
import { makeVT } from '../utils/verticals'
import ResponsiveShell from '../components/layout/ResponsiveShell'
import PushPermissionModal, { PUSH_PROMPT_DISMISSED_KEY } from '../components/push/PushPermissionModal'
import { isPushSupported, ensurePushSubscription } from '../lib/pushNotifications'
import { useLangPrefix } from '../hooks/useLangPrefix'
import useManagerStore from '../store/managerStore'
import { canAccess, normalizeRole, RBAC_MODULES } from '../lib/rbac'
import { getDashboardSummary } from '../api/dashboard'

function todayStr() {
  const d = new Date()
  const dd = String(d.getDate()).padStart(2, '0')
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const yyyy = d.getFullYear()
  return `${dd}/${mm}/${yyyy}`
}

function Skeleton({ className = '' }) {
  return <div aria-hidden="true" className={`bg-gray-200/80 rounded-md animate-pulse ${className}`} />
}

const TIME_BY_REPORT_TYPE = {
  day: 'today',
  week: 'last7Days',
  month: 'thisMonth',
}

function formatUpdatedAt(value, locale = 'vi-VN') {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return new Intl.DateTimeFormat(locale, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date)
}

function formatTrend(trend, t) {
  if (!trend || trend.delta === 0) return t('dashboard.trendFlat')
  const prefix = trend.delta > 0 ? '+' : ''
  return t('dashboard.trendChange', { pct: `${prefix}${trend.pct}` })
}

function InsightCard({ label, value, sub, loading = false }) {
  return (
    <div className="card p-3 min-h-[92px]" aria-busy={loading}>
      <div className="text-text-hint text-xs">{label}</div>
      {loading ? (
        <div className="mt-2 space-y-2">
          <Skeleton className="h-6 w-20" />
          <Skeleton className="h-3 w-28 max-w-full" />
        </div>
      ) : (
        <>
          <div className="text-lg font-black text-primary mt-1">{value}</div>
          {sub && <div className="text-text-hint text-xs mt-1">{sub}</div>}
        </>
      )}
    </div>
  )
}

function StatCard({ label, value, sub, trend, onClick, loading = false }) {
  const content = (
    <>
      <div className="flex items-start">
        <span className="text-text-hint text-xs flex-1">{label}</span>
        <span className="text-text-hint text-sm">›</span>
      </div>
      {loading ? (
        <div className="mt-2 space-y-2">
          <Skeleton className="h-8 w-16" />
          <div className="flex items-center justify-between gap-3">
            <Skeleton className="h-3 w-20" />
            <Skeleton className="h-3 w-12" />
          </div>
        </div>
      ) : (
        <>
          <div className="stat-num mt-1">{value}</div>
          <div className="flex items-center justify-between gap-2 mt-1">
            <div className="text-text-hint text-xs">{sub}</div>
            {trend && <div className="text-[11px] font-semibold text-primary">{trend}</div>}
          </div>
        </>
      )}
    </>
  )

  if (onClick) {
    return (
      <button type="button" onClick={onClick} disabled={loading} aria-busy={loading} className="card p-3 text-left w-full min-h-[100px] disabled:cursor-default">
        {content}
      </button>
    )
  }

  return (
    <div className="card p-3 min-h-[100px]" aria-busy={loading}>
      {content}
    </div>
  )
}

const CREDIT_BAR_CLASS = { ok: 'bg-green-500', warning: 'bg-amber-500', danger: 'bg-red-500' }
export default function DashboardPage() {
  const { t, i18n } = useTranslation()
  const tenantVertical = useTenantVertical()
  const vt = makeVT(t, tenantVertical)
  const navigate = useNavigate()
  const { prefix } = useLangPrefix()
  const { tenantId, role, creditStatus, setCreditStatus } = useManagerStore()
  const normalizedRole = normalizeRole(role)
  const canAccessBilling = canAccess(normalizedRole, RBAC_MODULES.billing)
  const [reportType, setReportType] = useState('day')
  const [timePreset, setTimePreset] = useState('today')
  const [sourceFilter, setSourceFilter] = useState('all')
  const [upcomingMinutes, setUpcomingMinutes] = useState(60)
  const [summary, setSummary] = useState(null)
  const [summaryLoading, setSummaryLoading] = useState(true)
  const [summaryError, setSummaryError] = useState(null)
  const [showPushModal, setShowPushModal] = useState(false)

  useEffect(() => {
    if (!tenantId) return
    if (typeof Notification === 'undefined') return

    if (Notification.permission === 'granted') {
      // Permission already granted (possibly from a session where the FCM
      // token never made it to the backend) — re-subscribe silently, no prompt.
      ensurePushSubscription(tenantId)
      return
    }

    if (Notification.permission !== 'default') return
    if (localStorage.getItem(PUSH_PROMPT_DISMISSED_KEY)) return
    let cancelled = false
    isPushSupported().then((supported) => {
      if (!cancelled && supported) setShowPushModal(true)
    })
    return () => { cancelled = true }
  }, [tenantId])

  const reportTypeOptions = useMemo(() => [
    { value: 'day', label: t('dashboard.byDay') },
    { value: 'week', label: t('dashboard.byWeek') },
    { value: 'month', label: t('dashboard.byMonth') },
  ], [t])

  const timeOptions = useMemo(() => [
    { value: 'today', label: t('dashboard.today') },
    { value: 'yesterday', label: t('dashboard.yesterday') },
    { value: 'last7Days', label: t('dashboard.last7Days') },
    { value: 'thisMonth', label: t('dashboard.thisMonth') },
  ], [t])

  const sourceOptions = useMemo(() => [
    { value: 'all', label: t('dashboard.allSources') },
    { value: 'web', label: t('dashboard.sourceWeb') },
    { value: 'facebook', label: t('dashboard.sourceFacebook') },
    { value: 'zalo', label: t('dashboard.sourceZalo') },
  ], [t])

  const upcomingOptions = useMemo(() => [
    { value: 60, label: t('dashboard.oneHour') },
    { value: 24 * 60, label: t('dashboard.todayUpcoming') },
    { value: 7 * 24 * 60, label: t('dashboard.next7Days') },
  ], [t])

  const loadSummary = useCallback(async ({ silent = false, force = false } = {}) => {
    if (!tenantId) return
    if (!silent) setSummaryLoading(true)
    setSummaryError(null)
    try {
      const data = await getDashboardSummary(tenantId, {
        timePreset,
        source: sourceFilter,
        upcomingMinutes,
        force,
      })
      setSummary({ ...data, __tenantId: tenantId })
      if (data?.creditStatus) setCreditStatus(data.creditStatus)
    } catch (err) {
      setSummary(previous => previous?.__tenantId === tenantId ? previous : null)
      setSummaryError(t('dashboard.loadFailed'))
      Sentry.captureException(err, {
        tags: { action: 'load_dashboard_summary' },
        extra: { tenantId, timePreset, sourceFilter, upcomingMinutes },
      })
    } finally {
      if (!silent) setSummaryLoading(false)
    }
  }, [tenantId, timePreset, sourceFilter, upcomingMinutes, setCreditStatus, t])

  useEffect(() => {
    loadSummary()
  }, [loadSummary])

  useEffect(() => {
    if (!tenantId) return undefined
    const interval = setInterval(() => loadSummary({ silent: true }), 60000)
    return () => clearInterval(interval)
  }, [tenantId, loadSummary])

  useEffect(() => {
    if (!tenantId) return undefined

    const refreshVisibleDashboard = () => {
      if (document.visibilityState && document.visibilityState !== 'visible') return
      loadSummary({ silent: true, force: true })
    }

    window.addEventListener('focus', refreshVisibleDashboard)
    document.addEventListener('visibilitychange', refreshVisibleDashboard)
    return () => {
      window.removeEventListener('focus', refreshVisibleDashboard)
      document.removeEventListener('visibilitychange', refreshVisibleDashboard)
    }
  }, [tenantId, loadSummary])

  // TODO(C17): no revenue money is shown here yet; when revenue KPIs land, format them with
  // formatMoney in the effective tenant currency and show the excluded_other_currency banner.
  const metrics = summary?.metrics || {}
  const trends = summary?.trends || {}
  const insights = summary?.insights || {}
  const hasCurrentTenantSummary = summary?.__tenantId === tenantId
  const isInitialLoading = !hasCurrentTenantSummary && (summaryLoading || !summaryError)
  const isRefreshing = summaryLoading && hasCurrentTenantSummary
  const selectedTimeLabel = timeOptions.find(option => option.value === timePreset)?.label || t('dashboard.today')
  const selectedUpcomingLabel = upcomingOptions.find(option => Number(option.value) === Number(upcomingMinutes))?.label || t('dashboard.oneHour')
  const busiestSourceLabel = sourceOptions.find(option => option.value === insights.busiestSource?.source)?.label || insights.busiestSource?.source || t('dashboard.dash')

  const billing          = billingView(creditStatus)
  const creditsTotal     = billing?.credits.total ?? 0
  const creditsRemaining = billing?.credits.remaining ?? 0
  const usedPct          = billing?.credits.usedPct ?? 0
  const cycleEnd         = formatBillingDate(billing?.cycleEnd, i18n.language)
  const barColor         = CREDIT_BAR_CLASS[billing?.tone ?? 'ok']

  function handleReportTypeChange(nextReportType) {
    setReportType(nextReportType)
    setTimePreset(TIME_BY_REPORT_TYPE[nextReportType] || 'today')
  }

  function buildDrilldownQuery(extra = {}) {
    const params = new URLSearchParams({
      range: timePreset,
      source: sourceFilter,
      ...extra,
    })
    return params.toString()
  }

  function goTo(path, extra) {
    navigate(`${prefix}${path}?${buildDrilldownQuery(extra)}`)
  }

  return (
    <>
      <PushPermissionModal
        open={showPushModal}
        onClose={() => setShowPushModal(false)}
        tenantId={tenantId}
      />
      <ResponsiveShell title={t('nav.dashboard')}>
      <div className="flex flex-col gap-3 py-3">

        {/* BLOCK 1 — Statistics filter */}
        <div className="card p-4">
          <div className="flex items-center justify-between">
            <span className="text-primary font-bold text-sm">{t('dashboard.statisticsFilter')}</span>
            <span className="text-primary text-sm font-semibold">{todayStr()}</span>
          </div>
          <div className="grid grid-cols-2 gap-2 mt-3">
            {[
              {
                label: t('dashboard.reportType'),
                value: reportType,
                onChange: handleReportTypeChange,
                options: reportTypeOptions,
              },
              {
                label: t('dashboard.time'),
                value: timePreset,
                onChange: setTimePreset,
                options: timeOptions,
              },
              {
                label: t('dashboard.source'),
                value: sourceFilter,
                onChange: setSourceFilter,
                options: sourceOptions,
              },
              {
                label: t('dashboard.upcomingIn'),
                value: upcomingMinutes,
                onChange: value => setUpcomingMinutes(Number(value)),
                options: upcomingOptions,
              },
            ].map(({ label, value, onChange, options }) => (
              <div key={label}>
                <div className="text-text-hint text-xs mb-1">{label}</div>
                <select
                  className="bg-page-bg rounded-lg px-3 py-2 text-xs font-semibold text-text-main w-full"
                  value={value}
                  disabled={isInitialLoading}
                  onChange={event => onChange(event.target.value)}
                >
                  {options.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
            ))}
          </div>
          {isInitialLoading ? (
            <Skeleton className="h-3 w-32 mt-3" />
          ) : summary?.meta?.lastUpdatedAt && (
            <div className="text-text-hint text-[11px] mt-3">
              {t('dashboard.lastUpdated', { time: formatUpdatedAt(summary.meta.lastUpdatedAt, i18n.language) })}
            </div>
          )}
        </div>

        {/* BLOCK 2 — Communication stats */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-primary font-bold text-sm">{t('dashboard.communicationStats')}</p>
            {summary?.meta?.partial && (
              <span className="text-[11px] text-amber-600">{t('dashboard.partialData')}</span>
            )}
            {isRefreshing && (
              <span className="inline-flex items-center gap-1.5 text-[11px] text-text-hint" role="status">
                <span className="h-2 w-2 rounded-full bg-primary animate-pulse" />
                {t('common.loading')}
              </span>
            )}
          </div>
          {summaryError && (
            <div className="mb-2 flex items-center justify-between gap-3 rounded-lg border border-red-100 bg-red-50 px-3 py-2 text-xs text-red-700" role="alert">
              <span>{summaryError}</span>
              <button type="button" className="shrink-0 font-semibold underline" onClick={() => loadSummary()}>
                {t('common.retry')}
              </button>
            </div>
          )}
          <div className="grid grid-cols-2 gap-2">
            <StatCard
              label={t('dashboard.totalMessages')}
              value={metrics.totalMessages ?? 0}
              sub={selectedTimeLabel}
              trend={formatTrend(trends.totalMessages, t)}
              loading={isInitialLoading}
              onClick={() => goTo('/inbox', { metric: 'messages' })}
            />
            <StatCard
              label={t('dashboard.newContacts')}
              value={metrics.newContacts ?? 0}
              sub={selectedTimeLabel}
              trend={formatTrend(trends.newContacts, t)}
              loading={isInitialLoading}
              onClick={() => goTo('/customers', { metric: 'new_contacts' })}
            />
          </div>
        </div>

        {/* BLOCK 3 — Booking management */}
        <div>
          <p className="text-primary font-bold text-sm mb-2">{vt('dashboard.bookingManagement')}</p>
          <div className="grid grid-cols-2 gap-2">
            <StatCard
              label={t('dashboard.totalBookings')}
              value={metrics.totalBookings ?? 0}
              sub={selectedTimeLabel}
              trend={formatTrend(trends.totalBookings, t)}
              loading={isInitialLoading}
              onClick={() => goTo('/bookings', { metric: 'total_bookings' })}
            />
            <StatCard
              label={vt('dashboard.upcomingGuests')}
              value={metrics.upcomingGuests ?? 0}
              sub={selectedUpcomingLabel}
              loading={isInitialLoading}
              onClick={() => goTo('/bookings', { metric: 'upcoming', upcomingMinutes })}
            />
            <StatCard
              label={t('dashboard.pendingBookings')}
              value={metrics.pendingBookings ?? 0}
              sub={t('dashboard.dash')}
              trend={formatTrend(trends.pendingBookings, t)}
              loading={isInitialLoading}
              onClick={() => goTo('/bookings', { status: 'pending' })}
            />
            <StatCard
              label={t('dashboard.confirmedBookings')}
              value={metrics.confirmedBookings ?? 0}
              sub={t('dashboard.dash')}
              trend={formatTrend(trends.confirmedBookings, t)}
              loading={isInitialLoading}
              onClick={() => goTo('/bookings', { status: 'confirmed' })}
            />
          </div>
        </div>

        {/* BLOCK 4 — Insights */}
        <div>
          <p className="text-primary font-bold text-sm mb-2">{t('dashboard.insights')}</p>
          <div className="grid grid-cols-2 gap-2">
            <InsightCard
              label={t('dashboard.busiestSource')}
              value={busiestSourceLabel}
              sub={insights.busiestSource?.count ? t('dashboard.sourceActivityCount', { count: insights.busiestSource.count }) : t('dashboard.dash')}
              loading={isInitialLoading}
            />
            <InsightCard
              label={t('dashboard.unreadConversations')}
              value={insights.unreadConversations ?? 0}
              sub={t('dashboard.needsAttention')}
              loading={isInitialLoading}
            />
            <InsightCard
              label={t('dashboard.staffMode')}
              value={insights.staffModeConversations ?? 0}
              sub={t('dashboard.aiPaused')}
              loading={isInitialLoading}
            />
            <InsightCard
              label={t('dashboard.bookingConversion')}
              value={`${insights.bookingConversionRate ?? 0}%`}
              sub={selectedTimeLabel}
              loading={isInitialLoading}
            />
          </div>
        </div>

        {/* BLOCK 5 — AI credits */}
        <div className="card p-4">
          <div className="text-text-hint text-xs uppercase tracking-wider">{t('dashboard.aiCreditsHeader')}</div>

          {creditStatus === null ? (
            <div className="mt-2 space-y-2">
              <Skeleton className="h-10 w-40" />
              <Skeleton className="h-1.5 w-full mt-3" />
              <Skeleton className="h-3 w-32 mt-2" />
            </div>
          ) : (
            <>
              <div className="flex items-baseline mt-1">
                <span className="text-4xl font-black text-primary">{creditsRemaining}</span>
                <span className="text-text-hint text-sm ml-2">{t('dashboard.creditsRemainingSlash', { total: creditsTotal })}</span>
              </div>
              <div className="w-full h-1.5 bg-gray-100 rounded-full mt-3">
                <div
                  className={`h-1.5 rounded-full ${barColor}`}
                  style={{ width: `${usedPct}%` }}
                />
              </div>
              <div className="flex justify-between mt-2">
                <span className="text-text-hint text-xs">{cycleEnd ? t('dashboard.resetsDate', { date: cycleEnd }) : ''}</span>
                {canAccessBilling && (
                  <button
                    className="text-primary text-xs font-semibold"
                    onClick={() => navigate(`${prefix}/billing`)}
                  >
                    {t('dashboard.topUp')}
                  </button>
                )}
              </div>
              {canAccessBilling && !billing?.isInternal && (
                <button
                  className="mt-3 w-full text-xs text-primary underline"
                  onClick={() => navigate(`${prefix}/billing`)}
                >
                  Xem chi tiết & nạp tín dụng →
                </button>
              )}
            </>
          )}
        </div>

      </div>
      </ResponsiveShell>
    </>
  )
}
